import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Component, useState, useEffect, useRef, type ReactNode } from 'react';
import { WifiOff, Paperclip } from 'lucide-react';
import type {
  MessageBlock,
  FileListBlock,
  ToolStepBlock,
} from '@netsuite-ai/types';
import { CitationPill } from '../blocks/CitationPill';
import { ActionPreview } from '../blocks/ActionPreview';
import { DataResult } from '../blocks/DataResult';
import { RecordEmbed } from '../blocks/RecordEmbed';
import { BulkEditBlock } from '../blocks/BulkEdit/BulkEditBlock';
import { ReasoningTrace } from '../blocks/ReasoningTrace';
import { ApprovalRequired } from '../blocks/ApprovalRequired';
import { useChatStore } from '../../store/chat.store';
import { fetchConversationFiles } from '../../lib/api';
import { SuggestedNextSteps } from '../blocks/SuggestedNextSteps';
import { ChartBlock } from '../blocks/ChartBlock';
import { ToolActivity } from '../blocks/ToolStep';

interface Props {
  blocks: MessageBlock[];
  status?: 'streaming' | 'complete' | 'error' | 'disconnected' | 'cancelled';
  toolCallLabel?: string | null;
  onRetry?: () => void;
  isLast?: boolean;
  onSuggestionClick?: (prompt: string) => void;
}

class BlockErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) {
      return (
        <p className="my-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
          This block couldn’t be displayed.
        </p>
      );
    }
    return this.props.children;
  }
}

type RenderItem =
  | { kind: 'block'; block: MessageBlock }
  | { kind: 'steps'; steps: ToolStepBlock[] };

/** Consecutive tool steps share one activity card; everything else renders alone. */
function groupSteps(blocks: MessageBlock[]): RenderItem[] {
  const items: RenderItem[] = [];
  for (const block of blocks) {
    const last = items[items.length - 1];
    if (block.type !== 'tool_step') items.push({ kind: 'block', block });
    else if (last?.kind === 'steps') last.steps.push(block);
    else items.push({ kind: 'steps', steps: [block] });
  }
  return items;
}

function MarkdownText({ text }: { text: string }) {
  return (
    <div className="prose prose-sm prose-p:my-2 prose-p:leading-relaxed prose-ul:my-2 prose-li:my-0.5 prose-headings:text-ink prose-p:text-ink prose-li:text-ink prose-strong:text-ink prose-a:text-primary prose-a:underline prose-a:underline-offset-2 prose-code:text-ink prose-code:before:content-none prose-code:after:content-none prose-pre:bg-ink/8 prose-pre:text-ink max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  );
}

export function StreamMessage({
  blocks,
  status,
  toolCallLabel,
  onRetry,
  isLast,
  onSuggestionClick,
}: Props) {
  const streaming = status === 'streaming';
  const hasContent = blocks.length > 0;
  const [showProgress, setShowProgress] = useState(false);
  const [liveText, setLiveText] = useState('');
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const prevStreamingRef = useRef(streaming);
  const prevBlockCountRef = useRef(0);

  // Show thin progress bar if first token hasn't arrived after 600ms
  useEffect(() => {
    if (!streaming || hasContent) {
      setShowProgress(false);
      return;
    }
    const t = setTimeout(() => setShowProgress(true), 600);
    return () => clearTimeout(t);
  }, [streaming, hasContent]);

  // Announce to screen readers when streaming completes
  useEffect(() => {
    if (prevStreamingRef.current && !streaming) {
      const text = blocksRef.current
        .filter((b) => b.type === 'text')
        .map((b) => (b as { type: 'text'; text: string }).text)
        .join(' ');
      if (text) setLiveText(text.slice(0, 300));
    }
    prevStreamingRef.current = streaming;
  }, [streaming]);

  // Announce non-text block arrivals (e.g. "data result", "approval required")
  useEffect(() => {
    if (blocks.length > prevBlockCountRef.current) {
      const newest = blocks[blocks.length - 1];
      if (newest && newest.type !== 'text')
        setLiveText(newest.type.replace(/_/g, ' '));
      prevBlockCountRef.current = blocks.length;
    }
  }, [blocks.length]);

  return (
    <div className="text-sm leading-relaxed">
      {showProgress && (
        <div className="bg-ink/8 relative mb-2 h-0.5 overflow-hidden rounded-full">
          <div className="bg-primary/40 absolute inset-y-0 w-1/3 rounded-full motion-safe:animate-[slide-indeterminate_1.5s_linear_infinite] motion-reduce:w-full" />
        </div>
      )}

      {groupSteps(blocks).map((item, i) =>
        item.kind === 'steps' ? (
          <BlockErrorBoundary key={item.steps[0].id}>
            <ToolActivity steps={item.steps} />
          </BlockErrorBoundary>
        ) : (
          <BlockErrorBoundary
            key={
              'id' in item.block && item.block.id ? item.block.id : `text-${i}`
            }
          >
            <BlockRenderer
              block={item.block}
              isLast={isLast}
              onSuggestionClick={onSuggestionClick}
            />
          </BlockErrorBoundary>
        ),
      )}

      {streaming && !toolCallLabel && (
        <span
          className="opacity-40 motion-safe:animate-[blink-cursor_1s_step-end_infinite]"
          aria-hidden="true"
        >
          {' ▍'}
        </span>
      )}

      {status === 'error' && !hasContent && (
        <div role="alert" className="flex items-center gap-3">
          <p className="text-sm text-red-500 dark:text-red-400">
            Something went wrong. Please try again.
          </p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="rounded border border-red-200 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50 active:bg-red-100 dark:border-red-900/60 dark:text-red-300 dark:hover:bg-red-950/40 dark:active:bg-red-950/60"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {status === 'disconnected' && (
        <div
          role="alert"
          className="flex flex-col items-center justify-center gap-6 px-6 py-12 text-center"
        >
          <WifiOff size={100} strokeWidth={2.5} className="text-ink" />
          <div className="space-y-2">
            <p className="text-xl font-bold text-neutral-900">
              Couldn't reach the assistant
            </p>
            <p className="mx-auto max-w-xs text-sm text-neutral-500">
              The service is temporarily unavailable. Your conversation isn't
              lost — try again in a moment.
            </p>
          </div>
          {onRetry && (
            <button
              onClick={onRetry}
              className="rounded border border-neutral-300 px-6 py-2 text-sm font-medium text-neutral-900 hover:bg-neutral-50 active:bg-neutral-100"
            >
              Retry
            </button>
          )}
        </div>
      )}

      <span
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {liveText}
      </span>
    </div>
  );
}

// Signed GET URLs are short-lived, so a link is minted at click time rather
// than at render — a URL fetched on mount would be dead by the time a user
// scrolled back to it.
function AttachmentList({
  files,
}: {
  files: { fileId: string; name: string }[];
}) {
  const conversationId = useChatStore((s) => s.activeConversationId);
  const [openingId, setOpeningId] = useState<string | null>(null);

  async function open(fileId: string) {
    if (!conversationId) return;
    setOpeningId(fileId);
    try {
      const all = await fetchConversationFiles(conversationId);
      const match = all.find((f) => f.fileId === fileId);
      if (match) window.open(match.url, '_blank', 'noopener,noreferrer');
    } catch {
      /* the file may still be uploading — leave the chip inert */
    } finally {
      setOpeningId(null);
    }
  }

  return (
    <ul className="mt-2 flex flex-wrap gap-2">
      {files.map((f) => (
        <li key={f.fileId}>
          <button
            type="button"
            onClick={() => open(f.fileId)}
            disabled={openingId === f.fileId}
            className="bg-ink/5 text-ink/70 hover:bg-ink/10 hover:text-ink flex max-w-full items-center gap-1.5 rounded-sm px-2 py-1 text-xs transition-colors disabled:opacity-50"
          >
            <Paperclip size={12} strokeWidth={2} className="shrink-0" />
            <span className="truncate">{f.name}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

// Files stored against a record. A file attached this turn has no view URL yet —
// list_files supplies it once the upload has completed — so render it unlinked.
function FileList({ block }: { block: FileListBlock }) {
  return (
    <ul className="mt-2 flex flex-wrap gap-2">
      {block.files.map((f) => (
        <li
          key={f.fileId}
          className="bg-ink/5 text-ink/70 flex max-w-full items-center gap-1.5 rounded-sm px-2 py-1 text-xs"
        >
          <Paperclip size={12} strokeWidth={2} className="shrink-0" />
          {f.url ? (
            <a
              href={f.url}
              target="_blank"
              rel="noreferrer"
              className="truncate underline underline-offset-2"
            >
              {f.name}
            </a>
          ) : (
            <span className="truncate">{f.name}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function BlockRenderer({
  block,
  isLast,
  onSuggestionClick,
}: {
  block: MessageBlock;
  isLast?: boolean;
  onSuggestionClick?: (prompt: string) => void;
}) {
  switch (block.type) {
    case 'text':
      return <MarkdownText text={block.text} />;
    case 'citation_pill':
      return <CitationPill block={block} />;
    case 'action_preview':
      return <ActionPreview block={block} />;
    case 'data_result':
      return <DataResult block={block} />;
    case 'record_embed':
      return <RecordEmbed block={block} />;
    case 'bulk_edit':
      return <BulkEditBlock block={block} />;
    case 'reasoning_trace':
      return <ReasoningTrace block={block} />;
    case 'approval_required':
      return <ApprovalRequired block={block} />;
    case 'attachment_list':
      return <AttachmentList files={block.files} />;
    case 'file_list':
      return <FileList block={block} />;
    case 'suggested_next_steps':
      // Only the latest turn's suggestions stay actionable.
      return isLast ? (
        <SuggestedNextSteps block={block} onPick={onSuggestionClick} />
      ) : null;
    case 'chart':
      return <ChartBlock block={block} />;
    default:
      return null;
  }
}
